import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccessControl } from '../../contexts/AccessControlContext';
import { NAV_GROUPS } from '../../rbac/navConfig';
import { isNavRouteVisible } from '../../rbac/navAccess';

type NavItem = { to: string; labelKey: string; icon: React.ReactNode; end?: boolean };
type NavGroup = { titleKey?: string; items: NavItem[] };
type FloatTip = { label: string; top: number; left: number };

const NAV_ICONS: Record<string, React.ReactNode> = {
  '/tasks': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  '/tasks/attending': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  '/tasks/assign': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M7 7h10" />
      <path d="M7 12h10" />
      <path d="M7 17h6" />
    </svg>
  ),
  '/tasks/case-details': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </svg>
  ),
  '/tasks/escalation': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  '/dashboards': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 14v4" />
      <path d="M12 10v8" />
      <path d="M17 6v12" />
    </svg>
  ),
  '/dashboards/sg': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 14v4" />
      <path d="M12 10v8" />
      <path d="M17 6v12" />
    </svg>
  ),
  '/dashboards/operational': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 14v4" />
      <path d="M12 10v8" />
      <path d="M17 6v12" />
    </svg>
  ),
  '/mail-room/requests': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M22 12H2" />
      <path d="M5 12l-3 3 3 3" />
      <path d="M19 12l3-3-3-3" />
    </svg>
  ),
  '/record-room/completed-files': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M21 16V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8" />
      <path d="M3 16h18" />
      <path d="M8 6V4h8v2" />
    </svg>
  ),
  '/record-room/qr-scanning-log': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11z" />
      <path d="M8 8h8v8H8z" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </svg>
  ),
  '/legal-knowledge/archive': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M8 7h8" />
      <path d="M8 11h8" />
    </svg>
  ),
  '/legal-knowledge/archive/management/dashboard': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 14v4" />
      <path d="M12 10v8" />
      <path d="M17 6v12" />
    </svg>
  ),
  '/legal-knowledge/archive/management/outcomes': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </svg>
  ),
  '/admin/intranet-users': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  '/admin/user-categories': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 6h16" />
      <path d="M4 12h10" />
      <path d="M4 18h16" />
      <circle cx="18" cy="12" r="2" />
    </svg>
  ),
  '/admin/extranet-users': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 12h18" />
      <path d="M12 3v18" />
      <path d="M4 7h4" />
      <path d="M16 17h4" />
    </svg>
  ),
  '/admin/roles-permissions': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 1l3 5 5 .5-3.5 3.5.9 5.5L12 13l-5.4 3 .9-5.5L4 6.5 9 6z" />
      <path d="M7 22h10" />
    </svg>
  ),
  '/audit-logs': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 12h6" />
      <path d="M9 16h6" />
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M13 2v7h7" />
    </svg>
  ),
  '/reports/user-performance': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  '/reports/case-tracking': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>
  ),
  '/reports/workflow-performance': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="5" r="2" />
      <circle cx="5" cy="19" r="2" />
      <circle cx="19" cy="19" r="2" />
      <path d="M12 7v4" />
      <path d="M7 17l3-4" />
      <path d="M17 17l-3-4" />
    </svg>
  ),
  '/workflow/templates': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 3H5a2 2 0 0 0-2 2v4" />
      <path d="M15 3h4a2 2 0 0 1 2 2v4" />
      <path d="M9 21H5a2 2 0 0 1-2-2v-4" />
      <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
      <path d="M7 8h10" />
      <path d="M7 12h10" />
      <path d="M7 16h6" />
    </svg>
  ),
  '/metadata/subcategories': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 4h7v7H4z" />
      <path d="M13 4h7v4h-7z" />
      <path d="M13 10h7v10h-7z" />
      <path d="M4 13h7v7H4z" />
    </svg>
  ),
  '/metadata/courts': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 21h18" />
      <path d="M6 21V7l6-4 6 4v14" />
      <path d="M10 21v-6h4v6" />
      <path d="M10 9h4" />
    </svg>
  ),
  '/metadata/languages': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="m5 8 6 6" />
      <path d="m4 14 6-6 2-3" />
      <path d="M2 5h12" />
      <path d="M7 2h1v12" />
    </svg>
  ),
  '/metadata/countries': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  ),
  '/metadata/nationalities': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="12" r="2" />
      <path d="M15 10h4" />
      <path d="M15 14h4" />
    </svg>
  ),
  '/metadata/police-stations': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 21h18" />
      <path d="M6 21V9l6-4 6 4v12" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </svg>
  ),
  '/metadata/letter-templates': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8" />
      <path d="M8 17h8" />
      <path d="M8 9h2" />
    </svg>
  ),
  '/metadata/subsidiary-tasks': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      <path d="M3 16h4" />
      <path d="M3 12h2" />
    </svg>
  ),
  '/metadata/case-scenarios': (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
};

const SIDENAV_NARROW_MAX_PX = 920;

export function SideNav() {
  const { t } = useTranslation();
  const ac = useAccessControl();
  const [floatTip, setFloatTip] = useState<FloatTip | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('agd.sidenav.collapsed');
      if (v === null) return true; /* default: collapsed */
      return v === '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${SIDENAV_NARROW_MAX_PX}px)`);
    const onViewportChange = () => {
      if (mq.matches) setCollapsed(true);
    };
    onViewportChange();
    mq.addEventListener('change', onViewportChange);
    return () => mq.removeEventListener('change', onViewportChange);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('agd.sidenav.collapsed', collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed]);

  const showFloatTip = useCallback(
    (el: HTMLElement, label: string) => {
      if (!collapsed) return;
      const r = el.getBoundingClientRect();
      setFloatTip({ label, top: r.top + r.height / 2, left: r.right + 10 });
    },
    [collapsed],
  );

  const hideFloatTip = useCallback(() => setFloatTip(null), []);

  const visibleFloatTip = collapsed ? floatTip : null;

  useEffect(() => {
    if (!visibleFloatTip) return;
    const hide = () => setFloatTip(null);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [visibleFloatTip]);

  const groups = useMemo<NavGroup[]>(
    () =>
      NAV_GROUPS.map((g) => ({
        titleKey: g.titleKey,
        items: g.items.map((item) => ({
          ...item,
          icon: NAV_ICONS[item.to] ?? null,
        })),
      })),
    [],
  );

  const visibleGroups = useMemo<NavGroup[]>(() => {
    const ctx = { roleCode: ac.roleCode, permissions: ac.permissions };
    const allowItem = (to: string): boolean => isNavRouteVisible(to, ctx);
    return groups
      .map((g) => ({ ...g, items: g.items.filter((i) => allowItem(i.to)) }))
      .filter((g) => g.items.length > 0);
  }, [ac, groups]);

  return (
    <aside className={`side-nav ${collapsed ? 'is-collapsed' : ''}`} aria-label={t('nav.mainNavigation')}>
      <div className="side-nav-inner">
        <div className="side-nav-top">
          <button
            className="side-nav-toggle"
            type="button"
            onClick={() => {
              if (collapsed) setFloatTip(null);
              setCollapsed((v) => !v);
            }}
            aria-label={collapsed ? t('nav.expandNavigation') : t('nav.collapseNavigation')}
            onMouseEnter={(e) => showFloatTip(e.currentTarget, t('nav.expandNavigation'))}
            onMouseLeave={hideFloatTip}
            onFocus={(e) => showFloatTip(e.currentTarget, t('nav.expandNavigation'))}
            onBlur={hideFloatTip}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 6h18" />
              <path d="M3 12h18" />
              <path d="M3 18h18" />
            </svg>
          </button>
        </div>

        {visibleGroups.map((g) => (
          <div key={g.titleKey ?? g.items.map((i) => i.to).join('|')} className="side-nav-group">
            {g.titleKey ? <div className="side-nav-group-title">{t(g.titleKey)}</div> : null}
            {g.items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                title={collapsed ? t(i.labelKey) : undefined}
                onMouseEnter={(e) => showFloatTip(e.currentTarget, t(i.labelKey))}
                onMouseLeave={hideFloatTip}
                onFocus={(e) => showFloatTip(e.currentTarget, t(i.labelKey))}
                onBlur={hideFloatTip}
                className={({ isActive }) => `side-nav-link ${isActive ? 'on' : ''}`}
              >
                <span className="side-nav-ico" aria-hidden="true">
                  {i.icon}
                </span>
                <span className="side-nav-txt">{t(i.labelKey)}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>
      {visibleFloatTip
        ? createPortal(
            <div
              className="side-nav-float-tip"
              role="tooltip"
            style={{ top: visibleFloatTip.top, left: visibleFloatTip.left }}
            >
            {visibleFloatTip.label}
            </div>,
            document.body,
          )
        : null}
    </aside>
  );
}
